import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import {
  X, ArrowRight, ArrowLeft, Sparkles, CheckCircle, Loader2,
  Play, Pause, ExternalLink, Building2, Wallet, Users, ClipboardCheck,
  FileStack, MapPinned, ShoppingBag, ListChecks, KeyRound, Settings2, LayoutDashboard,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const CHAPTERS = [
  {
    icon: Sparkles, chapter: "Intro",
    title: "Welcome to Iris Realty",
    body: "A guided tour of your property investment & development operating system. In under 3 minutes you'll see how Iris covers deal sourcing, refurb management, vendor payments, snagging, sales — all with an AI co-pilot.",
    tagline: "Chapter 1 of 12 · Iris Realty",
    accent: "sage",
  },
  {
    icon: LayoutDashboard, chapter: "Dashboard",
    title: "Your executive dashboard",
    body: "Every day starts here. See capital deployed split between assets and refurb spend, active projects, pending approvals waiting on you, and projected profit across the entire portfolio. Iris AI writes a weekly executive briefing pointing out what's off-track.",
    tagline: "Chapter 2 of 12 · /app/dashboard",
    link: "/app/dashboard",
    linkLabel: "Open live dashboard",
    accent: "sage",
  },
  {
    icon: Building2, chapter: "Pipeline",
    title: "Property pipeline — grouped by asset class",
    body: "Every property flows through Sourcing → Under Offer → Owned → Refurbishing → Listed → Sold. Properties are grouped by Residential / Commercial / Land. Drag cards between stages to update in real time.",
    tagline: "Chapter 3 of 12 · /app/properties",
    link: "/app/properties",
    linkLabel: "Open the pipeline",
    accent: "sage",
  },
  {
    icon: Sparkles, chapter: "AI Deal Analyzer",
    title: "Iris AI valuates every deal",
    body: "Paste a Bayut listing URL — or fill in the form. Iris AI returns fair purchase price, after-refurb value, refurb budget, deal score, guardrails (target offer, walk-away price, max refurb spend) and recent sold comparables from DLD, PF, and Bayut. Ask a clarifying question and it will refine the analysis interactively.",
    tagline: "Chapter 4 of 12 · /app/deal-analyzer",
    link: "/app/deal-analyzer",
    linkLabel: "Try the analyzer",
    accent: "sage",
  },
  {
    icon: FileStack, chapter: "BOQ Comparison",
    title: "AI-audited BOQs & fraud detection",
    body: "Upload BOQs from multiple vendors. Iris normalizes them line-by-line and audits each for inflated rates, quantity mismatches, duplicate items, and missing standard items. Every BOQ gets a fraud score 0-100. Marina Heights has 2 vendors compared — one clean, one flagged.",
    tagline: "Chapter 5 of 12 · Property → BOQs tab",
    accent: "gold",
  },
  {
    icon: ClipboardCheck, chapter: "Approvals",
    title: "Multi-step approval workflow",
    body: "Vendor submits an invoice → Accountant reviews → PM approves → MD approves → Payments executes. Every action is auditable and time-stamped. Users only see invoices that need their attention.",
    tagline: "Chapter 6 of 12 · /app/approvals",
    link: "/app/approvals",
    linkLabel: "Open the inbox",
    accent: "sage",
  },
  {
    icon: Users, chapter: "Vendors",
    title: "Vendors + one-click portal accounts",
    body: "Every vendor has a rating, TRN, trade specialization, and performance scorecard (speed, cost accuracy). Create a login for any vendor with one click — Iris generates a temp password you share, they log in at /vendor-portal.",
    tagline: "Chapter 7 of 12 · /app/vendors",
    link: "/app/vendors",
    linkLabel: "Manage vendors",
    accent: "sage",
  },
  {
    icon: KeyRound, chapter: "Vendor Portal",
    title: "Vendors see only their own data",
    body: "External vendors sign in at /vendor-portal and see ONLY their invoices, snags, BOQs, and the properties they're scoped to — nothing else. Demo login: karim@marinacontracting.demo / DemoVendor2026!",
    tagline: "Chapter 8 of 12 · /vendor-portal",
    link: "/vendor-portal",
    linkLabel: "Open Vendor Portal",
    external: true,
    accent: "gold",
  },
  {
    icon: MapPinned, chapter: "Drawings & Permits",
    title: "Every drawing revision + authority submission",
    body: "Track drawings from Arch, MEP, Interior. Log submissions to Dubai Municipality, Civil Defence, DEWA, RTA, Developer NOC. See fees paid, approval dates, and stamped copies — all in one place.",
    tagline: "Chapter 9 of 12 · /app/drawings",
    link: "/app/drawings",
    linkLabel: "Open Drawings & Permits",
    accent: "sage",
  },
  {
    icon: ListChecks, chapter: "Tasks",
    title: "Priority-grouped tasks with progress notes",
    body: "Assign tasks to anyone, group by Priority (High → Medium → Low) or by Property. Each task keeps a running log of daily progress notes — who commented, when, and what they said. Overdue tasks are flagged in red.",
    tagline: "Chapter 10 of 12 · /app/tasks",
    link: "/app/tasks",
    linkLabel: "Open Tasks",
    accent: "sage",
  },
  {
    icon: Settings2, chapter: "Admin",
    title: "Admin Settings — every dropdown configurable",
    body: "Owner and MD control every dropdown across the platform: property types, vendor trades, expense categories, communities, and finance defaults (mortgage rate, LTV, DLD fees, VAT). Change once — it applies everywhere.",
    tagline: "Chapter 11 of 12 · /app/admin",
    link: "/app/admin",
    linkLabel: "Open Admin Settings",
    accent: "gold",
  },
  {
    icon: CheckCircle, chapter: "Wrap-up",
    title: "That's Iris Realty",
    body: "One platform — deal sourcing, refurb, snagging, accounting, drawings, vendor portal, AI co-pilot, RBAC. Everything is audited, every doc is versioned, and Iris AI has full context of your portfolio. Explore freely — the demo workspace is fully reloadable from the header.",
    tagline: "Chapter 12 of 12 · You're ready",
    link: "/app/dashboard",
    linkLabel: "Enter the platform",
    accent: "sage",
  },
];

const AUTO_ADVANCE_MS = 14000;

export default function DemoTour() {
  const { workspace, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/demo/status').then(r => setSeeded(r.data.seeded)).catch(() => {});
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!autoplay) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setStep(s => (s + 1 >= CHAPTERS.length ? 0 : s + 1));
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timerRef.current);
  }, [autoplay]);

  const c = CHAPTERS[step];
  const progressPct = ((step + 1) / CHAPTERS.length) * 100;

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await api.post('/demo/seed');
      setSeeded(true);
      if (workspace !== 'demo') switchWorkspace('demo');
    } finally { setSeeding(false); }
  };

  const goStep = (idx) => {
    setStep(Math.max(0, Math.min(CHAPTERS.length - 1, idx)));
  };

  const openLink = () => {
    if (!c.link) return;
    if (c.external) window.open(c.link, '_blank');
    else navigate(c.link);
  };

  const Icon = c.icon || Sparkles;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="demo-tour">
      <div className="ambient-bg"/>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-8 py-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center overflow-hidden p-2">
            <img src="/iris-icon.svg" alt="Iris" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-serif text-xl">Iris Realty · Guided Tour</div>
            <div className="overline text-[0.6rem]">Property investment OS</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAutoplay(a => !a)} data-testid="tour-autoplay"
                  className="btn-ghost text-sm flex items-center gap-2">
            {autoplay ? <><Pause size={14}/> Pause auto-play</> : <><Play size={14}/> Auto-play</>}
          </button>
          <Link to="/app/dashboard" className="btn-ghost text-sm flex items-center gap-2" data-testid="tour-skip">
            <X size={14}/> Skip tour
          </Link>
        </div>
      </div>

      {/* Progress track */}
      <div className="relative z-10 px-8 mb-4">
        <div className="h-1 rounded-full bg-white/40 overflow-hidden">
          <motion.div
            className="h-full bg-[#679286]"
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
        <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-500 mt-1.5">
          <span>{c.tagline}</span>
          <span>{step + 1} / {CHAPTERS.length}</span>
        </div>
      </div>

      {/* Chapter grid */}
      <div className="relative z-10 grid grid-cols-12 gap-8 px-8 pb-8">
        {/* Chapter list */}
        <aside className="col-span-12 md:col-span-3">
          <div className="glass p-2 space-y-1 sticky top-4 max-h-[70vh] overflow-y-auto">
            {CHAPTERS.map((ch, i) => {
              const CI = ch.icon;
              const active = i === step;
              return (
                <button
                  key={i}
                  onClick={() => goStep(i)}
                  data-testid={`tour-chapter-${i}`}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-colors ${
                    active ? 'bg-[#1A2530] text-white' : 'text-slate-700 hover:bg-white/60'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex-shrink-0 grid place-items-center text-[10px] ${active ? 'bg-white/20' : 'bg-slate-200'}`}>{i + 1}</span>
                  <CI size={12} className="flex-shrink-0"/>
                  <span className="truncate">{ch.chapter}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main chapter card */}
        <main className="col-span-12 md:col-span-9">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.35 }}
              className="glass-strong p-10 min-h-[440px] flex flex-col"
              data-testid="tour-chapter-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-2xl grid place-items-center ${c.accent === 'gold' ? 'bg-[#F0EEE7]' : 'bg-[#EEF2F0]'}`}>
                  <Icon size={22} className={c.accent === 'gold' ? 'text-[#93704A]' : 'text-[#679286]'}/>
                </div>
                <div className="overline">{c.chapter}</div>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4">{c.title}</h1>
              <p className="text-lg text-slate-700 leading-relaxed max-w-2xl">{c.body}</p>

              <div className="flex-1"/>

              <div className="flex items-center justify-between flex-wrap gap-3 mt-8 pt-6 border-t border-slate-200/60">
                <div className="flex gap-2">
                  <button onClick={() => goStep(step - 1)} disabled={step === 0}
                          data-testid="tour-prev"
                          className="btn-ghost text-sm flex items-center gap-2 disabled:opacity-40">
                    <ArrowLeft size={13}/> Previous
                  </button>
                  <button onClick={() => goStep(step + 1)} disabled={step === CHAPTERS.length - 1}
                          data-testid="tour-next"
                          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
                    Next <ArrowRight size={13}/>
                  </button>
                </div>

                {c.link && (
                  <button onClick={openLink} data-testid="tour-open-link"
                          className="btn-gold text-sm flex items-center gap-2">
                    {c.external && <ExternalLink size={13}/>}
                    {c.linkLabel || 'Open live'}
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* First-run seed banner */}
          {!seeded && step <= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="mt-5 glass p-5 flex items-center gap-4 flex-wrap"
              data-testid="tour-seed-banner"
            >
              <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center flex-shrink-0">
                {seeding ? <Loader2 size={18} className="animate-spin text-amber-800"/> : <Sparkles size={18} className="text-amber-800"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">Demo data not seeded yet</div>
                <div className="text-xs text-slate-600">Load 9 properties, 5 vendors, 6 users, 4 invoices, 4 snags and full history so the tour feels real.</div>
              </div>
              <button onClick={seedDemo} disabled={seeding} className="btn-primary text-sm" data-testid="tour-seed-btn">
                {seeding ? 'Seeding…' : 'Seed demo data'}
              </button>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}
